import React, { useState, useEffect } from 'react';
import { Search, Clock, MapPin, Bell, ArrowRight, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface ArrivalCheck {
    id: number;
    creator_id: number;
    creator_username: string;
    creator_name?: string;
    creator_avatar?: string;
    target_id: number;
    target_username: string;
    target_name?: string;
    target_avatar?: string;
    place_id: number;
    place_name: string;
    expected_time?: string;
    days_of_week?: string;
    grace_period_minutes?: number;
    is_active: boolean;
    last_triggered?: string;
    created_at: string;
}

interface ArrivalStats {
    total_checks: number;
    active_checks: number;
    triggered_recently: number;
}

const ArrivalCheckManagement: React.FC = () => {
    const [checks, setChecks] = useState<ArrivalCheck[]>([]);
    const [stats, setStats] = useState<ArrivalStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [activeOnly, setActiveOnly] = useState(false);

    useEffect(() => {
        fetchData();
    }, [activeOnly]);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const url = activeOnly ? 'https://api.lifeshare.app/api/v1/admin/arrival-checks?active_only=true' : 'https://api.lifeshare.app/api/v1/admin/arrival-checks';
            const [checksRes, statsRes] = await Promise.all([
                axios.get(url),
                axios.get('https://api.lifeshare.app/api/v1/admin/arrival-checks/stats')
            ]);
            setChecks(checksRes.data.checks || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const formatDays = (days?: string) => {
        if (!days || days === 'all') return 'Her gün';
        const dayMap: Record<string, string> = { 'mon': 'Pzt', 'tue': 'Sal', 'wed': 'Çar', 'thu': 'Per', 'fri': 'Cum', 'sat': 'Cmt', 'sun': 'Paz' };
        return days.split(',').map(d => dayMap[d.trim().toLowerCase()] || d).join(', ');
    };

    const filteredChecks = checks.filter(c =>
        c.creator_username.toLowerCase().includes(searchText.toLowerCase()) ||
        c.target_username.toLowerCase().includes(searchText.toLowerCase()) ||
        c.place_name.toLowerCase().includes(searchText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Varış Kontrolleri</h1>
                        <p className="text-sm text-gray-500 mt-1">Hedef varış kontrol ayarları</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Clock className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.total_checks}</p>
                            <p className="text-xs text-purple-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Bell className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.active_checks}</p>
                            <p className="text-xs text-green-600">Aktif</p>
                        </div>
                        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-red-700">{stats.triggered_recently}</p>
                            <p className="text-xs text-red-600">Tetiklenen (7g)</p>
                        </div>
                    </div>
                )}

                {/* Search & Filter */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input type="text" placeholder="Kullanıcı veya yer ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                        </div>
                        <button onClick={() => setActiveOnly(!activeOnly)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeOnly ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                            Sadece Aktif
                        </button>
                    </div>
                </div>

                {/* List */}
                {filteredChecks.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Varış kontrolü bulunamadı</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredChecks.map((check) => (
                            <div key={check.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-center gap-3 flex-wrap">
                                    {/* Creator */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        {check.creator_avatar ? (
                                            <img src={`https://api.lifeshare.app${check.creator_avatar}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-sm font-medium">
                                                {(check.creator_name || check.creator_username)[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="font-medium text-gray-900 text-sm truncate">{check.creator_name || check.creator_username}</p>
                                            <p className="text-xs text-gray-500">İzliyor</p>
                                        </div>
                                    </div>

                                    <ArrowRight className="w-4 h-4 text-gray-400" />

                                    {/* Target */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        {check.target_avatar ? (
                                            <img src={`https://api.lifeshare.app${check.target_avatar}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
                                                {(check.target_name || check.target_username)[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="font-medium text-gray-900 text-sm truncate">{check.target_name || check.target_username}</p>
                                            <p className="text-xs text-gray-500">İzlenen</p>
                                        </div>
                                    </div>

                                    <ArrowRight className="w-4 h-4 text-gray-400" />

                                    {/* Place */}
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                            <MapPin className="w-4 h-4" />
                                        </div>
                                        <span className="font-medium text-gray-900 text-sm">{check.place_name}</span>
                                    </div>

                                    <span className={`ml-auto px-2 py-0.5 rounded-xl text-xs font-medium ${check.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                        {check.is_active ? 'Aktif' : 'Pasif'}
                                    </span>
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-4 text-xs text-gray-500">
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Beklenen: {check.expected_time || '-'}</span>
                                    <span>📅 {formatDays(check.days_of_week)}</span>
                                    <span>⏳ Tolerans: {check.grace_period_minutes || 10} dk</span>
                                    {check.last_triggered && <span className="text-red-600">🔔 Son tetik: {new Date(check.last_triggered).toLocaleDateString('tr-TR')}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArrivalCheckManagement;
